import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SITE_TYPES, saveSiteType, type SiteType } from '@/services/siteApi';

// "What kind of site are you building?", asked once before hosting starts. The Hey
// Joe account suggests the answer (an agency account: a talent agency site; a
// casting account: a casting company site; a client account: a production company
// or an ad agency), with every option one tap away. Joe, Nov 2 2026.

const INK = 'var(--hj-ink)';
const MUTED = 'var(--hj-ink-2)';
const panel: React.CSSProperties = { background: '#fff', border: '1px solid var(--hj-hairline-soft)', borderRadius: 20, padding: 'clamp(22px, 4vw, 40px)' };
const btnBase: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 48, padding: '12px 20px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 16, cursor: 'pointer', boxSizing: 'border-box' };
const primary: React.CSSProperties = { ...btnBase, background: INK, color: '#fff', border: 0 };
const secondary: React.CSSProperties = { ...btnBase, background: '#fff', color: INK, border: '1.5px solid var(--hj-ink)' };
const linkBtn: React.CSSProperties = { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: INK, textDecoration: 'underline', cursor: 'pointer', minHeight: 40, alignSelf: 'flex-start' };

const ACCOUNT: Partial<Record<SiteType, string>> = { agency: 'an agency account', casting: 'a casting account' };
const typeInfo = (id: SiteType) => SITE_TYPES.find((t) => t.id === id)!;

const SiteTypeQuestion: React.FC<{ suggested: SiteType[]; current?: SiteType | null; showAll?: boolean; onChosen: (t: SiteType) => void; onCancel?: () => void }> = ({ suggested, current = null, showAll = false, onChosen, onCancel }) => {
  const [all, setAll] = useState(showAll || suggested.length === 0);
  const [busy, setBusy] = useState<SiteType | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const choose = async (t: SiteType) => {
    setBusy(t); setErr(null);
    try { await saveSiteType(t); onChosen(t); } catch (e) { setErr((e as Error).message); }
    setBusy(null);
  };
  const spin = (t: SiteType) => (busy === t ? <Loader2 className="w-4 h-4 animate-spin" /> : null);
  const one = suggested.length === 1 ? suggested[0] : null;

  return (
    <section className="flex flex-col" style={{ ...panel, gap: 18 }} aria-labelledby="site-type-q">
      {!all && one && (
        <>
          <h1 id="site-type-q" className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(28px, 4.5vw, 40px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: INK }}>
            {ACCOUNT[one] ? `It looks like you have ${ACCOUNT[one]}.` : 'What kind of site are you building?'}
          </h1>
          <p className="m-0" style={{ fontSize: 18, lineHeight: 1.5, color: MUTED }}>Do you want to build a website for your {typeInfo(one).noun}?</p>
          <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
            <button type="button" disabled={!!busy} onClick={() => choose(one)} style={primary}>{spin(one)}Yes, build my {typeInfo(one).noun} site</button>
            <button type="button" disabled={!!busy} onClick={() => setAll(true)} style={secondary}>See all the options</button>
          </div>
        </>
      )}
      {!all && suggested.length > 1 && (
        <>
          <h1 id="site-type-q" className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(28px, 4.5vw, 40px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: INK }}>What kind of site are you building?</h1>
          <p className="m-0" style={{ fontSize: 18, lineHeight: 1.5, color: MUTED }}>
            Are you building a site for {suggested.map((t) => `${/^[aeiou]/i.test(typeInfo(t).noun) ? 'an' : 'a'} ${typeInfo(t).noun}`).join(' or ')}?
          </p>
          <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
            {suggested.map((t) => <button key={t} type="button" disabled={!!busy} onClick={() => choose(t)} style={primary}>{spin(t)}{typeInfo(t).label.replace(/^An? /, '').replace(/ site$/, '').replace(/^./, (c) => c.toUpperCase())}</button>)}
          </div>
          <button type="button" disabled={!!busy} onClick={() => setAll(true)} style={linkBtn}>See all the options</button>
        </>
      )}
      {all && (
        <>
          <h1 id="site-type-q" className="font-hj-display m-0" style={{ fontWeight: 800, fontSize: 'clamp(28px, 4.5vw, 40px)', lineHeight: 1.08, letterSpacing: '-0.02em', color: INK }}>What kind of site are you building?</h1>
          <p className="m-0" style={{ fontSize: 17, lineHeight: 1.5, color: MUTED }}>We set up your site and the designer for it. You can change this later in Settings.</p>
          <div className="grid" role="list" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {SITE_TYPES.map((t) => {
              const on = t.id === current;
              return (
                <button key={t.id} type="button" role="listitem" disabled={!!busy} onClick={() => choose(t.id)} aria-pressed={on}
                  className="flex flex-col text-left" style={{ gap: 4, padding: '16px 18px', minHeight: 88, borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit',
                    background: on ? 'var(--hj-beige)' : '#fff', color: on ? 'var(--hj-beige-ink)' : INK, border: `1.5px solid ${on ? 'var(--hj-ink)' : 'var(--hj-hairline)'}` }}>
                  <span className="flex items-center" style={{ gap: 8, fontWeight: 700, fontSize: 17 }}>{spin(t.id)}{t.label}</span>
                  <span style={{ fontSize: 14.5, lineHeight: 1.45, color: on ? 'var(--hj-beige-ink)' : MUTED }}>{t.note}</span>
                </button>
              );
            })}
          </div>
          {onCancel && <button type="button" onClick={onCancel} style={linkBtn}>Keep it as it is</button>}
        </>
      )}
      {err && <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-pink-ink)' }}>{err}</p>}
    </section>
  );
};

export default SiteTypeQuestion;
